import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { PencilIcon } from '#/shared/ui/icons'
import { UserDropdown } from '#/shared/ui/user-dropdown'
import type { UserOption } from '#/shared/ui/user-dropdown'

import { MoreMenuDropdown } from './more-menu-dropdown'
import type {
  ArchiveSubmenuConfig,
  ExtraMenuItem,
  MarkAsConfig,
  MoreMenuAction,
} from './more-menu-dropdown'
import { PostToLBDropdown } from './post-to-lb-dropdown'

interface DetailToolbarCompactProps {
  user: UserOption
  users?: UserOption[]
  showUser?: boolean
  onUserChange?: (user: UserOption) => void
  onEdit?: () => void
  onMoreAction?: (action: MoreMenuAction) => void
  extraMenuItems?: ExtraMenuItem[]
  markAs?: MarkAsConfig
  backToQuote?: MarkAsConfig
  archive?: ArchiveSubmenuConfig
  primaryAction?: React.ReactNode
  requests?: React.ReactNode
}

function EditButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-8 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-3 text-gray-500 transform-gpu transition-[transform,background-color,color,border-color] duration-150 ease-out hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98] focus-visible:outline-none focus-visible:focus-ring"
    >
      <PencilIcon className="size-3.5" />
    </button>
  )
}

export function DetailToolbarCompact({
  user,
  users,
  showUser,
  onUserChange,
  onEdit,
  onMoreAction,
  extraMenuItems,
  markAs,
  backToQuote,
  archive,
  primaryAction,
  requests,
}: DetailToolbarCompactProps) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center">
      {requests}
      {showUser && (
        <UserDropdown
          currentUser={user}
          users={users ?? [user]}
          onSelect={onUserChange}
          align="left"
        />
      )}
      <div className="ml-auto flex shrink-0 items-center gap-2.5 pl-2.5">
        {primaryAction ?? (
          <PostToLBDropdown label={t('detailToolbar.postToLB')} />
        )}
        {onEdit && (
          <EditButton label={t('detailToolbar.edit')} onClick={onEdit} />
        )}
        <MoreMenuDropdown
          label={t('detailToolbar.more')}
          onSelect={onMoreAction}
          extraItems={extraMenuItems}
          markAs={markAs}
          backToQuote={backToQuote}
          archive={archive}
          archiveAlways
          triggerClassName="text-gray-900"
        />
      </div>
    </div>
  )
}
